import React from 'react'
import Image from 'next/image'
import {trpc} from '@/trpc/trpc.client'
import {createAppAbility} from '@skillrecordings/skill-lesson/utils/ability'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {track} from '@skillrecordings/skill-lesson/utils/analytics'
import {twMerge} from 'tailwind-merge'

const useAbilities = () => {
  const {data: abilityRules} = trpc.abilities.getAbilities.useQuery()

  return createAppAbility(abilityRules || [])
}

const links = [
  {
    label: 'Articles',
    href: '/articles',
  },
]

type NavigationProps = {
  className?: string
}

const Navigation: React.FC<NavigationProps> = ({className}) => {
  const ability = useAbilities()
  const canViewTeam = ability.can('view', 'Team')
  const canViewInvoice = ability.can('view', 'Invoice')
  const {pathname, asPath} = useRouter()
  const isRoot = pathname === '/'

  return (
    <nav
      aria-label="top"
      className={twMerge(
        'mx-auto flex w-full max-w-screen-xl items-center justify-between p-5',
        className,
      )}
    >
      <Link
        href="/"
        aria-current={isRoot}
        tabIndex={isRoot ? -1 : 0}
        passHref
        className="flex items-center gap-1 text-base font-bold"
      >
        <Logo />
        {/* <Image
          src={require('../../../public/favicon.ico')}
          alt=""
          width={24}
          height={24}
        /> */}
      </Link>
      <ul className="flex items-center gap-5">
        {links.map(({href, label}) => {
          return (
            <li key={href}>
              <Link
                href={href}
                onClick={() => {
                  track(`clicked ${label} from navigation`)
                }}
                passHref
              >
                {label}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default Navigation

const Logo = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-40"
      fill="none"
      viewBox="0 0 143 40"
    >
      <path
        fill="#02AC45"
        d="M40.821 16V4.05h-4.249V1.7h11.052v2.35h-4.25V16h-2.553Zm6.238 0V5.173h2.308v.94h.04c.47-.715 1.288-1.144 2.289-1.144.49 0 .919.06 1.307.184l-.429 2.39c-.45-.123-.837-.184-1.164-.184-1.267 0-1.9.858-1.9 2.574V16h-2.451Zm6.396-2.88c0-1.818 1.082-2.8 3.86-3.453 1.819-.47 2.39-.797 2.39-1.47 0-.757-.653-1.226-1.736-1.226-1.225 0-2.063.715-2.288 1.92l-2.308-.49c.184-1.063.674-1.92 1.512-2.554.837-.654 1.88-.98 3.084-.98 2.615 0 4.25 1.49 4.25 3.922V16h-2.227v-.94h-.041c-.552.797-1.614 1.246-2.942 1.246-2.145 0-3.554-1.246-3.554-3.186Zm6.353-.777v-1.859h-.041c-.225.389-.735.675-1.88 1.001-1.49.389-2.063.797-2.063 1.471 0 .817.532 1.226 1.594 1.226 1.41 0 2.39-.756 2.39-1.839ZM64.355 16V5.173h2.35v.96h.04c.633-.756 1.818-1.266 2.983-1.266 2.45 0 4.044 1.654 4.044 4.187V16h-2.451V9.442c0-1.45-.817-2.288-2.227-2.288s-2.288.9-2.288 2.288V16h-2.451Zm10.958-3.555 2.226-.572c.348 1.635 1.165 2.35 2.738 2.35 1.164 0 1.9-.43 1.9-1.103 0-.838-.756-1.246-2.574-1.798-2.37-.674-3.657-1.307-3.739-3.248 0-1.818 1.716-3.207 3.984-3.207 1.083 0 2.043.286 2.86.878.817.592 1.348 1.348 1.593 2.308l-2.247.634c-.347-1.144-1.144-1.737-2.206-1.737-1.001 0-1.655.43-1.655 1.083 0 .572.633.878 2.166 1.307 2.533.715 4.085 1.492 4.147 3.677 0 1.982-1.676 3.29-4.23 3.29-2.757 0-4.555-1.39-4.963-3.861Zm10.418-1.859c0-3.39 2.125-5.72 5.169-5.72 2.553 0 4.412 1.533 4.82 3.964l-2.349.449c-.245-1.39-1.143-2.165-2.471-2.165-1.655 0-2.717 1.348-2.717 3.472 0 2.125 1.062 3.473 2.717 3.473 1.368 0 2.226-.776 2.471-2.165l2.35.45c-.409 2.43-2.268 3.962-4.821 3.962-3.044 0-5.169-2.328-5.169-5.72ZM97.451 16V5.173h2.309v.94h.04c.47-.715 1.288-1.144 2.289-1.144.49 0 .919.06 1.307.184l-.429 2.39c-.449-.123-.838-.184-1.164-.184-1.267 0-1.9.858-1.9 2.574V16H97.45Zm7.282 0V5.173h2.451V16h-2.451Zm-.061-12.666V.863h2.574v2.471h-2.574Zm4.769 17.262V5.173h2.39v.858h.041c.715-.736 1.757-1.164 2.962-1.164 2.819 0 4.862 2.41 4.862 5.72 0 3.309-2.043 5.72-4.841 5.72-1.206 0-2.288-.45-2.922-1.165h-.041v5.454h-2.451Zm7.804-10.01c0-2.145-1.042-3.472-2.758-3.472-1.491 0-2.595 1.123-2.595 2.594v1.736c0 .756.246 1.37.736 1.88.49.49 1.103.735 1.818.735 1.736 0 2.799-1.327 2.799-3.473Zm10.072-3.227h-2.677v5.373c0 .714.266.919 1.144.919h1.083V16h-1.307c-2.431 0-3.371-.858-3.371-3.146V7.359h-1.777V5.173h1.777V2.538h2.451v2.635h2.677v2.186Z"
      />
      <path
        fill="#000"
        d="m46.01 36-.838-2.451h-5.801L38.533 36H35.96l5.148-14.3h2.35L48.583 36H46.01Zm-5.863-4.698h4.25l-2.105-6.068h-.04l-2.105 6.067Zm9.32.96v-7.09h2.452v6.579c0 1.532.715 2.308 2.165 2.308 1.328 0 2.207-.919 2.207-2.308v-6.578h2.451V36h-2.37v-.96h-.04c-.634.797-1.778 1.266-2.983 1.266-2.451 0-3.881-1.49-3.881-4.044Zm17.502-4.903h-2.676v5.372c0 .715.265.92 1.144.92h1.082V36h-1.307c-2.431 0-3.37-.858-3.37-3.146v-5.495h-1.778v-2.186h1.777v-2.635h2.452v2.635h2.676v2.186Zm.708 3.227c0-3.411 2.145-5.72 5.25-5.72 3.125 0 5.27 2.309 5.27 5.72 0 3.412-2.145 5.72-5.27 5.72-3.105 0-5.25-2.308-5.25-5.72Zm8.07 0c0-2.144-1.043-3.472-2.82-3.472-1.737 0-2.799 1.287-2.799 3.473 0 2.185 1.063 3.472 2.799 3.472 1.777 0 2.82-1.328 2.82-3.472ZM80.034 36V25.173h2.309v.92h.04c.593-.757 1.635-1.227 2.758-1.227 1.124 0 2.207.532 2.799 1.349h.04c.757-.9 1.717-1.349 2.922-1.349 2.309 0 3.943 1.655 3.943 3.841V36h-2.39v-6.864c0-1.246-.756-2.022-1.92-2.022-1.165 0-1.941.837-1.941 2.124V36h-2.309v-6.864c0-1.246-.755-2.022-1.92-2.022-1.164 0-1.94.837-1.94 2.124V36h-2.39Zm16.63-2.88c0-1.818 1.083-2.8 3.862-3.453 1.818-.47 2.39-.797 2.39-1.47 0-.756-.654-1.226-1.737-1.226-1.226 0-2.063.715-2.288 1.92l-2.308-.49c.184-1.063.674-1.92 1.512-2.554.837-.654 1.879-.98 3.084-.98 2.615 0 4.249 1.49 4.249 3.922V36h-2.226v-.94h-.041c-.552.797-1.614 1.246-2.942 1.246-2.145 0-3.554-1.246-3.554-3.186Zm6.354-.777v-1.859h-.041c-.225.388-.735.674-1.879 1.001-1.492.389-2.064.797-2.064 1.471 0 .817.531 1.226 1.594 1.226 1.409 0 2.39-.756 2.39-1.839Zm10.333-4.984h-2.676v5.372c0 .715.265.92 1.144.92h1.082V36h-1.307c-2.431 0-3.371-.858-3.371-3.146v-5.495h-1.777v-2.186h1.777v-2.635h2.452v2.635h2.676v2.186ZM114.947 36V25.173h2.452V36h-2.452Zm-.061-12.666v-2.471h2.574v2.471h-2.574Zm4.361 7.253c0-3.412 2.145-5.72 5.25-5.72 3.125 0 5.27 2.308 5.27 5.72 0 3.411-2.145 5.72-5.27 5.72-3.105 0-5.25-2.309-5.25-5.72Zm8.069 0c0-2.145-1.042-3.473-2.819-3.473-1.737 0-2.799 1.287-2.799 3.473 0 2.185 1.062 3.472 2.799 3.472 1.777 0 2.819-1.328 2.819-3.472ZM131.605 36V25.173h2.349v.96h.041c.633-.756 1.818-1.267 2.983-1.267 2.451 0 4.045 1.655 4.045 4.188V36h-2.452v-6.558c0-1.45-.817-2.288-2.227-2.288-1.409 0-2.288.9-2.288 2.288V36h-2.451Z"
      />
      <path
        stroke="#02AC45"
        stroke-miterlimit="10"
        stroke-width="2"
        d="m16.001 19 4-3h7.5a1.5 1.5 0 0 0 1.5-1.501V7.5a1.5 1.5 0 0 0-1.5-1.5H17.5A1.5 1.5 0 0 0 16 7.5l.001 11.5Z"
      />
      <path
        fill="#000"
        fill-rule="evenodd"
        d="M20.214 21H21a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V23a2 2 0 0 1 2-2h8v-2H4a4 4 0 0 0-4 4v10a4 4 0 0 0 4 4h17a4 4 0 0 0 4-4V23a4 4 0 0 0-2.402-3.668L20.213 21Z"
        clip-rule="evenodd"
      />
      <path
        stroke="#000"
        stroke-linecap="square"
        stroke-miterlimit="10"
        stroke-width="2"
        d="M7 18v-4m0 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      />
      <path
        fill="#000"
        d="M17 28a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm-9 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
      />
    </svg>
  )
}
